import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { can, ROLE_HOME, type Permission, type StaffUser } from '@sebastian/shared';
import { api, AUTH_EXPIRED_EVENT, tokenStore } from './api';

interface AuthState {
  user: StaffUser | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  signIn: (email: string, password: string) => Promise<StaffUser>;
  signOut: () => Promise<void>;
  /** True when the signed-in role holds the permission. */
  allows: (permission: Permission) => boolean;
  home: string;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<StaffUser | null>(null);
  const [status, setStatus] = useState<AuthState['status']>('loading');

  // Restore the session on boot; an expired token simply lands on /login.
  useEffect(() => {
    let cancelled = false;
    if (!tokenStore.get()) {
      setStatus('anonymous');
      return;
    }
    api.auth
      .me()
      .then((account) => {
        if (cancelled) return;
        setUser(account);
        setStatus('authenticated');
      })
      .catch(() => {
        if (cancelled) return;
        tokenStore.clear();
        setStatus('anonymous');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Any 401 anywhere in the app drops us back to anonymous.
  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      setStatus('anonymous');
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const session = await api.auth.login(email, password);
    tokenStore.set(session.token);
    setUser(session.user);
    setStatus('authenticated');
    return session.user;
  }, []);

  const signOut = useCallback(async () => {
    await api.auth.logout().catch(() => undefined);
    tokenStore.clear();
    setUser(null);
    setStatus('anonymous');
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      status,
      signIn,
      signOut,
      allows: (permission) => (user ? can(user.role, permission) : false),
      home: user ? ROLE_HOME[user.role] : '/login',
    }),
    [user, status, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
